import { Switch } from '@base-ui/react';
import styled from '@emotion/styled';

import type { SwitchSize } from './types';

const SIZES: Record<SwitchSize, { width: number; height: number; thumbSize: number }> = {
  sm: { width: 28, height: 16, thumbSize: 12 },
  md: { width: 34, height: 20, thumbSize: 16 },
};

const THUMB_OFFSET = 2;

const noDollarProp = (prop: string) => !prop.startsWith('$');

export const StyledRoot = styled(Switch.Root, { shouldForwardProp: noDollarProp })<{
  $size: SwitchSize;
}>(({ theme, $size }) => {
  const { width, height } = SIZES[$size];

  return {
    // Layout & dimensions — the track container
    position: 'relative',
    display: 'inline-flex',
    width,
    height,
    padding: 0,
    flexShrink: 0,
    borderRadius: theme.radius.full,
    boxSizing: 'border-box',
    verticalAlign: 'middle',

    // Appearance — unchecked (default) state
    backgroundColor: theme.colors.background.overlay,

    // Behaviour
    cursor: 'pointer',
    userSelect: 'none',
    outline: 'none',
    border: 'none',

    // Transitions
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',

    // Checked state
    '&[data-checked]': {
      backgroundColor: theme.colors.brand.solid,
    },

    // Checked + hover
    '&[data-checked]:hover:not([data-disabled])': {
      backgroundColor: theme.colors.brand.hover,
    },

    // Disabled state — applied regardless of checked/unchecked
    '&[data-disabled]': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },

    // Focus glow — regardless of checked/unchecked
    '&:focus-visible:not([data-disabled])': {
      boxShadow: `0px 0px 0px 2px ${theme.colors.border.focus}`,
    },
  };
});

export const StyledThumb = styled(Switch.Thumb, { shouldForwardProp: noDollarProp })<{
  $size: SwitchSize;
}>(({ theme, $size }) => {
  const { width, thumbSize } = SIZES[$size];
  const translateX = width - thumbSize - THUMB_OFFSET * 2;

  return {
    // Thumb is the sliding circle
    position: 'absolute',
    top: THUMB_OFFSET,
    left: THUMB_OFFSET,
    width: thumbSize,
    height: thumbSize,
    borderRadius: theme.radius.full,

    backgroundColor: theme.colors.text.primary,
    boxShadow: theme.shadows.xs,

    // Prevent the thumb from capturing pointer events — the root handles those
    pointerEvents: 'none',

    // Smooth slide animation
    transition: 'transform 0.15s ease',

    // Checked: slide to the right
    '&[data-checked]': {
      transform: `translateX(${translateX}px)`,
    },
  };
});
